// Table.jsx
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSort,
    faTimes,
    faList,
    faThLarge,
} from "@fortawesome/free-solid-svg-icons";

import InlineLoader from "../Loader/InlineLoader";
import TableSearch from "./TableSearch";
import Select from "../Form/Fields/Select";
import Form from "../Form/Form";
import { eventBus } from "../../bus/eventBus";
import { useAuth } from "../../context/AuthProvider";

import "./Table.scss";
import { API_BASE_URL } from '../../api';

/**
 * TABLE PRO
 *
 * Modes:
 * mode="client" => tout en JS
 * mode="server" => backend gère pagination/search/filter/sort
 * mode="auto"   => détecte selon props
 */

const resolveId = (row, rowId) =>
    rowId.split('.').reduce((obj, key) => obj?.[key], row);

// How close to the bottom of the scroll container triggers loading the next page. Shared with
// scrollNearBottom's imperative method below, which deliberately stops short of this zone —
// landing inside it would immediately auto-trigger an unwanted extra page load right after
// restoring a ?page= from the URL.
const LOAD_MORE_THRESHOLD_PX = 300;

// Stable per-column identity for matching a column across renders. Many columns (custom
// display-only cells) have no `accessor` at all — falling back to `accessor` alone would make
// every accessor-less column collide on the same lookup, since they'd all share `undefined`.
const columnKey = (col, index) => col.accessor || `__col_${index}`;

// Table/card view segmented toggle — only rendered when the caller opts into card view
// (enableCardView) since a page needs to actually define its card design (renderCard) for
// that mode to be worth offering.
const ViewModeToggle = ({ viewMode, setViewMode }) => (
    <div style={{ display: 'flex', borderRadius: 8, padding: 2, gap: 2 }}>
        {[
            { mode: 'table', icon: faList, label: 'Liste' },
            { mode: 'card', icon: faThLarge, label: 'Cartes' },
        ].map(({ mode, icon, label }) => (
            <button
                key={mode}
                type="button"
                onClick={() => setViewMode(mode)}
                data-tooltip-id="tooltip"
                data-tooltip-content={label}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // 26px + the group's 2px padding (+ 1px each side) = 32px, level with the buttons
                    width: 30,
                    height: 26,
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    // ghost, like the toolbar buttons: active mode on a light gray fill
                    background: viewMode === mode ? '#f1f5f9' : 'transparent',
                    color: viewMode === mode ? '#0f172a' : '#94a3b8',
                }}
            >
                <FontAwesomeIcon icon={icon} style={{ fontSize: 13 }} />
            </button>
        ))}
    </div>
);

// Reapplies a saved { order, visibility } column layout (from a ui-bookmark or a saved view)
// onto the table's current column defaults. Shared so both the column bookmark and "views"
// stay consistent, and so columns added since the layout was saved still show up (appended,
// in their natural position) instead of silently disappearing.
const mergeColumnConfig = (colDefaults, saved) => {
    const isNewFormat = saved && (Array.isArray(saved.order) || (saved.visibility && typeof saved.visibility === 'object'));
    const visibility = isNewFormat ? (saved.visibility || {}) : (saved || {});
    const savedOrder = isNewFormat && Array.isArray(saved.order) ? saved.order : [];

    const byKey = new Map(colDefaults.map(col => [col.__key, col]));
    const ordered = [];
    savedOrder.forEach(key => {
        const col = byKey.get(key);
        if (col) {
            ordered.push({ ...col, visible: key in visibility ? visibility[key] : col.visible });
            byKey.delete(key);
        }
    });
    byKey.forEach(col => ordered.push({
        ...col,
        visible: col.__key in visibility ? visibility[col.__key] : col.visible,
    }));
    return ordered;
};

const Table = forwardRef(({
    columns = [],
    data = [],

    // mode
    mode = "auto",

    // server props
    totalElements = 0,
    onSort,
    onLoadMore,
    hasMore = false,
    sortField,
    sortDirection = "asc",
    onFilter,
    // Reports roughly how far down the loaded content the user has scrolled, as a 0..1
    // fraction (0 = top, 1 = bottom) — debounced. Table.js doesn't know how many pages are
    // loaded (EntityTable owns that), so it hands off the fraction and lets the caller turn
    // it into a page number (see EntityTable's ?page= restoration).
    onScrollFraction,

    // shared
    loading = false,
    // first load: this many placeholder rows shaped like the real ones (shimmer bars) instead
    // of the spinner — 0 keeps the spinner. A column can set `skeleton: 'media'` to get a
    // thumbnail + two lines (e.g. the product name column).
    skeletonRows = 0,
    rowId = "id",
    multiSelect = false,
    onSelectionChange,

    // search
    searchable = true,
    autoFocusSearch = false,
    keyword = "",
    setKeyword,

    // filters
    filters = {},

    // render — card view is opt-in per Table usage; when on, renderCard lets that usage
    // define its own card design (falls back to a generic key/value card if omitted)
    enableCardView = false,
    renderCard,
    // optional controlled table/card mode, for callers that render the toggle themselves
    // (e.g. no search toolbar) — overrides the internal / bookmarked mode
    viewMode: controlledViewMode,

    // row hover highlight
    hoverable = true,

    // double-click on a row (ignored when the checkbox/actions cell is the target)
    onRowDoubleClick,

    // single click on a row (ignored when the checkbox/actions cell is the target)
    onRowClick,

    // extra className(s) to add to a row based on its data, e.g. highlighting a
    // matched/associated row — returns a string (or falsy) to append
    rowClassName,

    // drag-and-drop a row out of the table (e.g. onto the cart trigger) — returns the
    // payload to attach to the drag event, or falsy to make that row non-draggable
    getRowDragData,
    dragMimeType = "application/x-saydalani-row",

    // selection actions shown in the banner
    selectionActions = [],

    // export
    onExport,
    onReset,

    searchPlaceholder = "Search...",
    showColumnsButton = true,
    showExportButton = true,
    showViewsButton = true,
    searchMaxWidth,
    voiceSearch = false,
    toolbarExtra,
    counterExtra,
    emptyMessage = "Aucune donnée disponible",

    // Remember column layout / view mode per user via ui-bookmarks (authenticated API).
    // Turn off on public pages, where an anonymous visitor would only get 401s.
    persistPreferences = true,

    // client mode
    pageSize = 10,
    // Client mode, for tables inside a panel/card (dashboard, product sheet...) rather than
    // filling the page: pageSize rows per page with a Précédent / Suivant pager under the
    // table, at natural height — instead of the default reveal-more-on-scroll, which needs a
    // height-bounded scroll container (full-page listings like Produits, Patients).
    pagination = false,
}, ref) => {
    const selectAllCheckboxRef = useRef(null);
    const { settings } = useAuth();
    const collapseHeaderVal = settings?.['general_general_settings_collapse_header_on_scroll'];
    const collapseHeaderOnScroll = collapseHeaderVal === true || collapseHeaderVal === 'true';
    /* ------------------------------------------------ */
    /* MODE */
    /* ------------------------------------------------ */

    const clientSide =
        mode === "client" ||
        (mode === "auto" && !onLoadMore && !onSort && !onFilter);

    /* ------------------------------------------------ */
    /* STATES */
    /* ------------------------------------------------ */

    const [internalViewMode, setViewMode] = useState("table");
    const viewMode = controlledViewMode ?? internalViewMode;
    // Below this width, the table's columns can't fit a phone screen even with horizontal
    // scroll (see the mobile app's own card-style lists) — so whenever a page offers a card
    // view, mobile forces it on and the "Vues" toggle (table/card) ref hidden, instead of
    // mutating `viewMode` itself (which stays the user's actual desktop preference, restored
    // from and persisted to their ui-bookmark below).
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const effectiveViewMode = (enableCardView && isMobile) ? 'card' : viewMode;
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectAllPages, setSelectAllPages] = useState(false);
    const [draggingRowId, setDraggingRowId] = useState(null);
    // collapses the search/toolbar bar (and, via eventBus, the page's title bar) while the
    // user scrolls down the table, giving more vertical room to the rows — reappears on
    // scroll-up or once back near the top
    const [chromeHidden, setChromeHidden] = useState(false);

    const colBookmarkKey = `cols:${columns.map((c, i) => columnKey(c, i)).sort().join(',')}`;
    const colDefaults = columns.map((col, i) => ({ ...col, visible: col.visible !== false, __key: columnKey(col, i) }));

    const [visibleColumns, setVisibleColumns] = useState(colDefaults);
    const isLoadingBookmark = useRef(true);
    const saveTimer = useRef(null);

    // Views (saved filter + column layout presets, see the VIEWS section below) —
    // declared up here so moveColumn can route through the wrapped setter that
    // invalidates the active view on a manual column change.
    const [views, setViews] = useState([]);
    const [activeViewId, setActiveViewId] = useState(null);
    const [viewsLoading, setViewsLoading] = useState(false);
    const isApplyingViewRef = useRef(false);

    // Manual column changes (drag-reorder, visibility toggle) live-update whichever saved
    // view is active, so the view "follows" further tweaks instead of getting deselected —
    // persistActiveViewUpdate (declared in the VIEWS section below; safe to reference here,
    // it's only actually called once the user interacts, by which point render has finished
    // and it's been assigned) skips this while applyView is programmatically restoring a
    // view's own layout, guarded by isApplyingViewRef.
    const updateVisibleColumns = (next) => {
        setVisibleColumns(next);
        if (!isApplyingViewRef.current && activeViewId) {
            persistActiveViewUpdate({ columnConfig: next });
        }
    };

    // Drag-to-reorder columns directly from the <th> headers (mirrors the same
    // reordering offered in the "Colonnes" panel — both act on visibleColumns).
    const [draggedColKey, setDraggedColKey] = useState(null);
    const [dragOverColKey, setDragOverColKey] = useState(null);

    const moveColumn = (fromKey, toKey) => {
        if (!fromKey || fromKey === toKey) return;
        const from = visibleColumns.findIndex((c) => c.__key === fromKey);
        const to = visibleColumns.findIndex((c) => c.__key === toKey);
        if (from === -1 || to === -1) return;
        const next = [...visibleColumns];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        updateVisibleColumns(next);
    };

    // Fetch saved column order/visibility from backend on mount. Older bookmarks were
    // saved as a flat { key: visible } map with no order — detect that shape and fall
    // back to the natural `columns` prop order, applying only the visibility flags.
    useEffect(() => {
        if (!persistPreferences) { isLoadingBookmark.current = false; return; }
        axios.get(`${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(colBookmarkKey)}`, { withCredentials: true })
            .then(res => {
                if (res.status === 200 && res.data) {
                    const saved = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
                    setVisibleColumns(mergeColumnConfig(colDefaults, saved));
                }
            })
            .catch(() => {})
            .finally(() => { isLoadingBookmark.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist column order/visibility changes to backend (debounced, skip initial load)
    useEffect(() => {
        if (isLoadingBookmark.current || !persistPreferences) return;
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            const payload = {
                order: visibleColumns.map(col => col.__key),
                visibility: Object.fromEntries(visibleColumns.map(col => [col.__key, col.visible])),
            };
            axios.put(
                `${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(colBookmarkKey)}`,
                JSON.stringify(payload),
                { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
            ).catch(() => {});
        }, 600);
        return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleColumns]);

    // client side states
    const [localDisplayCount, setLocalDisplayCount] = useState(pageSize);
    const [currentPage, setCurrentPage] = useState(0); // `pagination` mode only
    const [localSortField, setLocalSortField] = useState(null);
    const [localSortDirection, setLocalSortDirection] = useState("asc");
    // Seeded from the `filters` prop so a caller can pre-apply a default filter
    // in client mode (e.g. hide a category of rows until the user opts in via
    // the Filtres button) — TableSearch's "active" badge and the filter form's
    // prefilled values already read from that same prop.
    const [localFilters, setLocalFilters] = useState(filters);

    // internal keyword fallback when caller doesn't manage keyword state externally
    const [internalKeyword, setInternalKeyword] = useState('');
    const resolvedKeyword = setKeyword ? keyword : internalKeyword;
    const resolvedSetKeyword = setKeyword ?? setInternalKeyword;

    // infinite scroll refs
    const scrollRef          = useRef(null);
    const loadingRef         = useRef(loading);
    const hasMoreRef         = useRef(hasMore);
    const onLoadMoreRef      = useRef(onLoadMore);
    const clientHasMoreRef   = useRef(false);
    const localDisplayCountRef = useRef(pageSize);

    useImperativeHandle(ref, () => ({
        scrollToBottom: (behavior = 'smooth') => {
            const container = scrollRef.current;
            if (container) container.scrollTo({ top: container.scrollHeight, behavior });
        },
        // For "land where a restored ?page= left off" (EntityTable), not "jump to the true
        // end" — stopping inside the load-more trigger zone would fire an unwanted extra
        // page load the instant this runs, so this deliberately stays just outside it.
        scrollNearBottom: (margin = 200) => {
            const container = scrollRef.current;
            if (!container) return;
            const target = Math.max(0, container.scrollHeight - container.clientHeight - LOAD_MORE_THRESHOLD_PX - margin);
            container.scrollTo({ top: target, behavior: 'auto' });
        },
    }));

    useEffect(() => { loadingRef.current = loading; }, [loading]);
    useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
    useEffect(() => { onLoadMoreRef.current = onLoadMore; }, [onLoadMore]);

    const tryLoadMore = useCallback(() => {
        if (loadingRef.current) return;
        if (clientSide && clientHasMoreRef.current) {
            setLocalDisplayCount(prev => prev + pageSize);
        } else if (!clientSide && hasMoreRef.current) {
            onLoadMoreRef.current?.();
        }
    }, [clientSide, pageSize]);

    // Scroll listener — fires when user scrolls near the bottom of the container, and tracks
    // scroll direction to collapse/reveal the toolbar + page title bar
    const chromeHiddenRef = useRef(false);
    const lastScrollTopRef = useRef(0);
    // Kept in a ref (not the effect's deps) so a caller passing a fresh inline function each
    // render doesn't tear down/rebind the scroll listener every time.
    const onScrollFractionRef = useRef(onScrollFraction);
    useEffect(() => { onScrollFractionRef.current = onScrollFraction; }, [onScrollFraction]);
    const scrollFractionTimerRef = useRef(null);
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;
        const onScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const delta = scrollTop - lastScrollTopRef.current;
            const nearBottom = scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD_PX;

            if (nearBottom) tryLoadMore();

            // Recompute ?page= from scroll position in either direction — except right at the
            // loading edge, where loadMore just fired (or is about to): right after new rows
            // are appended there, that same (unmoved) scroll position suddenly reads as a
            // smaller fraction of the new, taller total, which would walk the page number
            // loadMore just correctly bumped straight back down. Everywhere else — including
            // scrolling back down through pages already loaded earlier — this should track
            // normally, or re-descending from a scroll-up never updates the URL again until
            // hitting genuinely new content.
            if (onScrollFractionRef.current && !nearBottom) {
                clearTimeout(scrollFractionTimerRef.current);
                scrollFractionTimerRef.current = setTimeout(() => {
                    const c = scrollRef.current;
                    if (!c) return;
                    const scrollable = c.scrollHeight - c.clientHeight;
                    const fraction = scrollable > 0 ? Math.min(1, Math.max(0, c.scrollTop / scrollable)) : 0;
                    onScrollFractionRef.current?.(fraction);
                }, 200);
            }

            if (collapseHeaderOnScroll) {
                let nextHidden = chromeHiddenRef.current;
                if (scrollTop < 24) nextHidden = false;
                else if (delta > 4) nextHidden = true;
                else if (delta < -4) nextHidden = false;

                if (nextHidden !== chromeHiddenRef.current) {
                    chromeHiddenRef.current = nextHidden;
                    setChromeHidden(nextHidden);
                    eventBus.emit('listingChromeVisibility', !nextHidden);
                }
            }
            lastScrollTopRef.current = scrollTop;
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            container.removeEventListener('scroll', onScroll);
            clearTimeout(scrollFractionTimerRef.current);
        };
        // effectiveViewMode: the scrollable node itself swaps (table's .table-responsive vs the
        // card grid's own scroll container) when the view is toggled, so the listener needs to
        // re-bind to whichever one is actually mounted.
    }, [tryLoadMore, collapseHeaderOnScroll, effectiveViewMode]);

    // If the setting gets turned off while the chrome is mid-collapse, snap it back open
    useEffect(() => {
        if (collapseHeaderOnScroll || !chromeHiddenRef.current) return;
        chromeHiddenRef.current = false;
        setChromeHidden(false);
        eventBus.emit('listingChromeVisibility', true);
    }, [collapseHeaderOnScroll]);

    // Restore the page title bar when this table unmounts (e.g. navigating away while collapsed)
    useEffect(() => {
        return () => { eventBus.emit('listingChromeVisibility', true); };
    }, []);

    // After each page loads, proactively load more if the content doesn't fill the container
    // — also re-checked on a view switch, since the card grid can pack the same rows into a
    // shorter scroll height than the table did.
    useEffect(() => {
        if (loading) return;
        const container = scrollRef.current;
        if (!container) return;
        if (container.scrollHeight <= container.clientHeight + 10) {
            tryLoadMore();
        }
    }, [loading, tryLoadMore, effectiveViewMode]);

    /* ------------------------------------------------ */
    /* SELECTION */
    /* ------------------------------------------------ */

    // Reset "select all pages" when keyword changes (new search = new context)
    useEffect(() => {
        setSelectAllPages(false);
        setSelectedIds([]);
    }, [resolvedKeyword]);

    useEffect(() => {
        onSelectionChange?.(selectedIds, { selectAll: selectAllPages, total: totalElements });
    }, [selectedIds, selectAllPages]);

    const toggleSelectOne = (id) => {
        setSelectAllPages(false);
        setSelectedIds((prev) =>
            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
        );
    };

    const toggleSelectAll = (rows) => {
        if (selectAllPages) {
            setSelectAllPages(false);
            setSelectedIds([]);
            return;
        }

        const ids = rows.map((r) => resolveId(r, rowId));
        const allSelected = ids.every((id) => selectedIds.includes(id));

        if (allSelected) {
            setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
        }
    };

    /* ------------------------------------------------ */
    /* CLIENT DATA PROCESSING */
    /* ------------------------------------------------ */

    const flattenValues = (obj) => {
        let values = [];

        Object.values(obj).forEach((value) => {
            if (value === null || value === undefined) return;

            if (typeof value === "object") {
                values = [...values, ...flattenValues(value)];
            } else {
                values.push(String(value));
            }
        });

        return values;
    };

    const processedData = useMemo(() => {
        if (!clientSide) return data;

        let rows = [...data];

        // keyword search
        if (resolvedKeyword?.trim()) {
            const search = resolvedKeyword.toLowerCase();

            rows = rows.filter((row) =>
                flattenValues(row).some((value) =>
                    value.toLowerCase().includes(search)
                )
            );
        }

        // filters
        Object.entries(localFilters).forEach(([key, filterValue]) => {
            if (filterValue === undefined || filterValue === null || filterValue === "") return;

            // Range filters (InputTextRange/InputDateRange) flatten to a "<field>Min"/"<field>Max"
            // key (see TableFilters.js) — those need an actual bound comparison against the
            // matching field, not the plain substring match below (which would, e.g., treat a
            // quantity min of "5" as a match for a stored quantity of "15").
            const rangeMatch = key.match(/^(.+)(Min|Max)$/);
            if (rangeMatch) {
                const [, field, bound] = rangeMatch;
                rows = rows.filter((row) => {
                    const raw = row[field];
                    if (raw === undefined || raw === null || raw === "") return false;
                    const rawNum = Number(raw);
                    const filterNum = Number(filterValue);
                    const cmp = (!Number.isNaN(rawNum) && !Number.isNaN(filterNum))
                        ? rawNum - filterNum
                        // Falls back to string comparison for non-numeric fields — works
                        // correctly for ISO "yyyy-MM-dd" dates, which sort lexicographically.
                        : (String(raw) < String(filterValue) ? -1 : String(raw) > String(filterValue) ? 1 : 0);
                    return bound === 'Min' ? cmp >= 0 : cmp <= 0;
                });
                return;
            }

            const needle = String(filterValue).toLowerCase();
            rows = rows.filter((row) =>
                flattenValues(row).some((cellValue) =>
                    cellValue.toLowerCase().includes(needle)
                )
            );
        });

        // sort
        if (localSortField) {
            rows.sort((a, b) => {
                const A = a[localSortField];
                const B = b[localSortField];

                if (A < B) return localSortDirection === "asc" ? -1 : 1;
                if (A > B) return localSortDirection === "asc" ? 1 : -1;
                return 0;
            });
        }

        return rows;
    }, [
        data,
        resolvedKeyword,
        localFilters,
        localSortField,
        localSortDirection,
        clientSide,
    ]);

    /* ------------------------------------------------ */
    /* DATA SLICE */
    /* ------------------------------------------------ */

    const computedTotalElements = clientSide
        ? processedData.length
        : totalElements;

    const paged = clientSide && pagination;
    const totalPages = paged ? Math.max(1, Math.ceil(processedData.length / pageSize)) : 1;
    const safePage = Math.min(currentPage, totalPages - 1);

    const clientHasMore = clientSide && !paged && localDisplayCount < processedData.length;
    clientHasMoreRef.current = clientHasMore;
    localDisplayCountRef.current = localDisplayCount;

    const paginatedData = !clientSide
        ? data
        : paged
            ? processedData.slice(safePage * pageSize, safePage * pageSize + pageSize)
            : processedData.slice(0, localDisplayCount);

    // reset display count / page when client data / filters change
    useEffect(() => {
        if (clientSide) {
            setLocalDisplayCount(pageSize);
            setCurrentPage(0);
        }
    }, [processedData.length, resolvedKeyword, localFilters, clientSide, pageSize]);

    /* ------------------------------------------------ */
    /* SORT */
    /* ------------------------------------------------ */

    const handleSort = (field) => {
        if (!field) return;

        if (clientSide) {
            if (localSortField === field) {
                setLocalSortDirection((prev) =>
                    prev === "asc" ? "desc" : "asc"
                );
            } else {
                setLocalSortField(field);
                setLocalSortDirection("asc");
            }
        } else {
            onSort?.(field);
        }
    };

    /* ------------------------------------------------ */
    /* FILTER */
    /* ------------------------------------------------ */

    const handleFilter = (values) => {
        if (!isApplyingViewRef.current && activeViewId) {
            persistActiveViewUpdate({ filters: values });
        }
        if (clientSide) {
            setLocalFilters(values);
        }
        // Called in both modes — server mode relies on this to actually (re)fetch, while
        // client mode uses it only as an optional hook for callers that want to persist
        // filters themselves (sessionStorage, etc.), since Table has no notion of a route/key
        // to persist against on its own.
        onFilter?.(values);
    };

    /* ------------------------------------------------ */
    /* VIEWS (saved filter + column layout presets) */
    /* ------------------------------------------------ */

    useEffect(() => {
        if (!showViewsButton) return;
        setViewsLoading(true);
        axios.get(`${API_BASE_URL}/api/v1/table-views?key=${encodeURIComponent(colBookmarkKey)}`, { withCredentials: true })
            .then(res => setViews(Array.isArray(res.data) ? res.data : []))
            .catch(() => {})
            .finally(() => setViewsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showViewsButton]);

    const currentFilters = clientSide ? localFilters : filters;

    // Debounced — a column drag or a few keystrokes in a filter field would otherwise fire one
    // PUT per intermediate change. `overrides` supplies whichever piece (filters or
    // columnConfig) just changed, straight from the caller so it's never stale; the other piece
    // falls back to this render's state, which is accurate since it didn't change in this
    // action. A second call within the debounce window (e.g. a filter edit right after a column
    // drag) cancels the first timer, and its own fallback already reflects the first change
    // (its state update landed before the next user action could fire), so the two merge
    // correctly into a single request either way.
    const viewSaveTimer = useRef(null);
    const persistActiveViewUpdate = (overrides = {}) => {
        if (!activeViewId) return;
        const viewId = activeViewId;
        clearTimeout(viewSaveTimer.current);
        viewSaveTimer.current = setTimeout(() => {
            const cols = overrides.columnConfig ?? visibleColumns;
            const flt = overrides.filters ?? currentFilters ?? {};
            const payload = {
                filters: JSON.stringify(flt),
                columnConfig: JSON.stringify({
                    order: cols.map(col => col.__key),
                    visibility: Object.fromEntries(cols.map(col => [col.__key, col.visible])),
                }),
            };
            axios.put(`${API_BASE_URL}/api/v1/table-views/${viewId}`, payload, { withCredentials: true })
                .then(({ data }) => setViews(prev => prev.map(v => (v.id === viewId ? data : v))))
                .catch(() => {});
        }, 600);
    };

    const applyView = (view) => {
        isApplyingViewRef.current = true;
        try {
            const parsedFilters = view.filters ? JSON.parse(view.filters) : {};
            const parsedColumns = view.columnConfig ? JSON.parse(view.columnConfig) : null;
            handleFilter(parsedFilters);
            if (parsedColumns) setVisibleColumns(mergeColumnConfig(colDefaults, parsedColumns));
            setActiveViewId(view.id);
        } catch {
            // Malformed saved view — ignore rather than crash the table.
        } finally {
            isApplyingViewRef.current = false;
        }
    };

    // "Vue par défaut" — leaves whichever view was active untouched (guarded the same way
    // applyView guards its own filter/column writes) and just clears back to no view, no
    // filters, natural column order.
    const resetToDefaultView = () => {
        isApplyingViewRef.current = true;
        try {
            handleFilter({});
            setVisibleColumns(colDefaults);
            setActiveViewId(null);
        } finally {
            isApplyingViewRef.current = false;
        }
    };

    const createView = async (name) => {
        const payload = {
            tableKey: colBookmarkKey,
            name,
            filters: JSON.stringify(currentFilters ?? {}),
            columnConfig: JSON.stringify({
                order: visibleColumns.map(col => col.__key),
                visibility: Object.fromEntries(visibleColumns.map(col => [col.__key, col.visible])),
            }),
        };
        const { data } = await axios.post(`${API_BASE_URL}/api/v1/table-views`, payload, { withCredentials: true });
        setViews(prev => [...prev, data]);
        setActiveViewId(data.id);
    };

    const deleteView = async (id) => {
        await axios.delete(`${API_BASE_URL}/api/v1/table-views/${id}`, { withCredentials: true });
        setViews(prev => prev.filter(v => v.id !== id));
        // Deleting the active view must also drop its applied filters/columns — not just the
        // "Vues" dropdown's own selection — otherwise the view's preferences stay in effect
        // even though the view itself no longer exists (and, if we merely cleared
        // activeViewId, any further filter/column change would 404 trying to PUT a deleted
        // view's id). resetToDefaultView() does exactly this reset, guarded the same way.
        if (activeViewId === id) resetToDefaultView();
    };

    const renameView = async (id, name) => {
        const { data } = await axios.put(`${API_BASE_URL}/api/v1/table-views/${id}`, { name }, { withCredentials: true });
        setViews(prev => prev.map(v => (v.id === id ? data : v)));
    };

    // Remember which view (if any) was active, the same way column layout is remembered —
    // as a ui-bookmark keyed per table — so a page refresh lands back on the same custom
    // view instead of silently falling back to the default one.
    const activeViewBookmarkKey = `activeView:${colBookmarkKey}`;
    const [savedActiveViewId, setSavedActiveViewId] = useState(undefined); // undefined = still loading, null = none saved
    const restoredViewRef = useRef(false);

    useEffect(() => {
        if (!showViewsButton) { setSavedActiveViewId(null); return; }
        axios.get(`${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(activeViewBookmarkKey)}`, { withCredentials: true })
            .then(res => {
                const saved = res.status === 200 && res.data
                    ? (typeof res.data === 'string' ? JSON.parse(res.data) : res.data)
                    : null;
                setSavedActiveViewId(saved?.id ?? null);
            })
            .catch(() => setSavedActiveViewId(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showViewsButton]);

    // Applies the saved view once both the views list and the bookmarked id are ready —
    // only ever runs once per mount (restoredViewRef), same one-shot restore pattern as the
    // column bookmark's isLoadingBookmark flag.
    useEffect(() => {
        if (restoredViewRef.current) return;
        if (viewsLoading || savedActiveViewId === undefined) return;
        restoredViewRef.current = true;
        if (savedActiveViewId != null) {
            const match = views.find(v => v.id === savedActiveViewId);
            if (match) applyView(match);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewsLoading, views, savedActiveViewId]);

    // Persist activeViewId changes back to the bookmark — gated on the restore having
    // already happened, so the initial (pre-restore) null doesn't clobber a saved bookmark
    // before it's even been read.
    useEffect(() => {
        if (!restoredViewRef.current || !persistPreferences) return;
        axios.put(
            `${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(activeViewBookmarkKey)}`,
            JSON.stringify({ id: activeViewId }),
            { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
        ).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeViewId]);

    // Same remember-across-refresh treatment for the table/card toggle — only relevant when
    // card view is actually offered (enableCardView); otherwise there's nothing to restore.
    const viewModeBookmarkKey = `viewMode:${colBookmarkKey}`;
    const restoredViewModeRef = useRef(false);

    useEffect(() => {
        if (!enableCardView || !persistPreferences || controlledViewMode) { restoredViewModeRef.current = true; return; }
        axios.get(`${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(viewModeBookmarkKey)}`, { withCredentials: true })
            .then(res => {
                const saved = res.status === 200 && res.data
                    ? (typeof res.data === 'string' ? JSON.parse(res.data) : res.data)
                    : null;
                if (saved?.mode === 'card') setViewMode('card');
            })
            .catch(() => {})
            .finally(() => { restoredViewModeRef.current = true; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableCardView]);

    useEffect(() => {
        if (!restoredViewModeRef.current || !persistPreferences || controlledViewMode) return;
        axios.put(
            `${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(viewModeBookmarkKey)}`,
            JSON.stringify({ mode: viewMode }),
            { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
        ).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);

    /* ------------------------------------------------ */
    /* RENDER */
    /* ------------------------------------------------ */

    const rows = paginatedData;
    // visibleColumns only tracks order/visibility (persisted as a bookmark) — it can hold column
    // objects captured from an earlier render, with stale Cell/Header closures over props/state
    // that have since changed (e.g. a status flip that should switch a cell from editable to
    // read-only). Re-merge with the current `columns` prop so rendering always uses fresh
    // closures; only the visible flag and ordering come from state.
    const displayedColumns = visibleColumns
        .filter((c) => c.visible)
        .map((c) => {
            const fresh = columns.find((orig, i) => columnKey(orig, i) === c.__key);
            return fresh ? { ...c, ...fresh } : c;
        });

    // Sync indeterminate state on the header checkbox (runs after every render)
    useEffect(() => {
        if (!selectAllCheckboxRef.current) return;
        const someSelected = rows.some((r) => selectedIds.includes(resolveId(r, rowId)));
        const allLoadedSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(resolveId(r, rowId)));
        selectAllCheckboxRef.current.indeterminate = someSelected && !allLoadedSelected && !selectAllPages;
    });

    const allLoadedSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(resolveId(r, rowId)));
    const bannerCount = selectAllPages ? computedTotalElements : selectedIds.length;

    return (
        <div className="table-pro" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {searchable && (
                <div style={{
                    flexShrink: 0,
                    maxHeight: chromeHidden ? 0 : 200,
                    opacity: chromeHidden ? 0 : 1,
                    overflow: 'hidden',
                    transition: 'max-height 0.25s ease, opacity 0.2s ease',
                }}>
                    <TableSearch
                        value={resolvedKeyword}
                        onChange={(e) => resolvedSetKeyword(e.target.value)}
                        columns={columns}
                        filters={currentFilters}
                        onFilter={handleFilter}
                        visibleColumns={visibleColumns}
                        setVisibleColumns={updateVisibleColumns}
                        // back to the listing's default columns (visibility + order); the persist effect
                        // then saves it over this user's ui-bookmark for the listing
                        onResetColumns={() => updateVisibleColumns(colDefaults)}
                        onExport={onExport}
                        onReset={() => {
                            persistActiveViewUpdate({ filters: {} });
                            // Server mode relies on the caller's onReset to clear its externally-owned
                            // filters state (EntityTable does this itself); client mode has no such
                            // external owner, so it must clear its own localFilters here or the
                            // "Réinitialiser" button would only reset the form's visible fields
                            // without ever re-filtering the data.
                            if (clientSide) setLocalFilters({});
                            onReset?.();
                        }}
                        placeholder={searchPlaceholder}
                        autoFocusSearch={autoFocusSearch}
                        loadedCount={rows.length}
                        totalElements={computedTotalElements}
                        showColumnsButton={showColumnsButton}
                        showExportButton={showExportButton}
                        showViewsButton={showViewsButton}
                        viewToggle={enableCardView && !isMobile ? <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} /> : null}
                        toolbarExtra={toolbarExtra}
                        counterExtra={counterExtra}
                        views={views}
                        viewsLoading={viewsLoading}
                        activeViewId={activeViewId}
                        onApplyView={applyView}
                        onCreateView={createView}
                        onDeleteView={deleteView}
                        onResetView={resetToDefaultView}
                        onRenameView={renameView}
                        {...(searchMaxWidth != null ? { searchMaxWidth } : {})}
                        voiceSearch={voiceSearch}
                    />
                </div>
            )}

            {/* SELECTION BANNER */}
            {multiSelect && (selectedIds.length > 0 || selectAllPages) && (
                <div className="selection-banner">
                    <div className="selection-banner-left">
                        <button
                            className="selection-banner-close"
                            onClick={() => { setSelectedIds([]); setSelectAllPages(false); }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        <span className="selection-banner-count">
                            <strong>{bannerCount}</strong> sélectionné{bannerCount > 1 ? "s" : ""}
                        </span>
                        {!selectAllPages && allLoadedSelected && computedTotalElements > rows.length && (
                            <button
                                className="select-all-pages-btn"
                                onClick={() => setSelectAllPages(true)}
                            >
                                Sélectionner les {computedTotalElements} résultats
                            </button>
                        )}
                        {selectAllPages && (
                            <span className="select-all-pages-info">— Tous les résultats sont inclus</span>
                        )}
                    </div>
                    <div className="selection-banner-actions">
                        {selectionActions.map((action, i) => (
                            <button
                                key={i}
                                className={`selection-action-btn ${action.variant || ""}`}
                                onClick={() => action.onClick(selectedIds, { selectAll: selectAllPages, total: computedTotalElements })}
                            >
                                {action.icon && <FontAwesomeIcon icon={action.icon} className="me-2" />}
                                {action.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}


            {/* TABLE */}
            <div className="table-pro-wrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {effectiveViewMode === "table" && (
                    <div className="table-responsive" ref={scrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', minHeight: 0 }}>
                        <table className={`table table_component mb-0${hoverable ? ' table_component--hoverable' : ''}${(selectAllPages || selectedIds.length > 0) ? ' table_component--selecting' : ''}`}>
                            <thead>
                                <tr>
                                    {multiSelect && (
                                        <th className="td-checkbox">
                                            <input
                                                ref={selectAllCheckboxRef}
                                                type="checkbox"
                                                className="form-check-input form-check-input-checkbox"
                                                checked={selectAllPages || allLoadedSelected}
                                                onChange={() => toggleSelectAll(rows)}
                                            />
                                        </th>
                                    )}

                                    {displayedColumns.map((col) => (
                                        <th
                                            key={col.__key}
                                            draggable
                                            style={{ cursor: col.sortable ? "pointer" : "default", minWidth: col.minWidth, maxWidth: col.maxWidth, width: col.width }}
                                            className={[
                                                col.activeColumn && "active",
                                                col.className,
                                                dragOverColKey === col.__key && draggedColKey !== col.__key && "col-drag-over",
                                                draggedColKey === col.__key && "col-dragging",
                                            ].filter(Boolean).join(" ")}
                                            onClick={() => col.sortable && handleSort(col.sortField ?? col.accessor)}
                                            onDragStart={(e) => {
                                                e.dataTransfer.effectAllowed = "move";
                                                e.dataTransfer.setData("text/plain", col.__key);
                                                setDraggedColKey(col.__key);
                                            }}
                                            onDragEnter={() => draggedColKey && setDragOverColKey(col.__key)}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                moveColumn(draggedColKey, col.__key);
                                                setDraggedColKey(null);
                                                setDragOverColKey(null);
                                            }}
                                            onDragEnd={() => { setDraggedColKey(null); setDragOverColKey(null); }}
                                        >
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                                {col.Header}
                                                {col.sortable && (
                                                    <FontAwesomeIcon icon={faSort} style={{ opacity: 0.5 }} />
                                                )}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {loading && rows.length === 0 && skeletonRows > 0 ? (
                                    Array.from({ length: skeletonRows }, (_, i) => (
                                        <tr key={`skeleton-${i}`} className="row-skeleton" aria-hidden="true">
                                            {multiSelect && (
                                                <td className="td-checkbox"><span className="skel skel--box" /></td>
                                            )}
                                            {displayedColumns.map((col, c) => (
                                                <td key={col.__key || c} className={col.className}>
                                                    {col.skeleton === 'media' ? (
                                                        <span className="skel-media">
                                                            <span className="skel skel--thumb" />
                                                            <span className="skel-media__lines">
                                                                {/* widths vary row to row so it doesn't read as a grid */}
                                                                <span className="skel skel--line" style={{ width: `${55 + ((i * 17) % 30)}%` }} />
                                                                <span className="skel skel--line skel--sm" style={{ width: `${30 + ((i * 11) % 20)}%` }} />
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        <span className="skel skel--line" style={{ width: `${45 + ((i * 13 + c * 7) % 40)}%` }} />
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : loading && rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={displayedColumns.length + 1}>
                                            <InlineLoader />
                                        </td>
                                    </tr>
                                ) : !loading && rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={displayedColumns.length + 1} className="table-empty">
                                            {emptyMessage}
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {rows.map((row, i) => {
                                            const dragData = getRowDragData?.(row);
                                            const rowKey = resolveId(row, rowId) || i;
                                            return (
                                            <tr
                                                key={rowKey}
                                                className={`${(selectAllPages || selectedIds.includes(resolveId(row, rowId))) ? "row-selected" : ""}${onRowClick ? " row-clickable" : ""}${dragData ? " row-draggable" : ""}${draggingRowId === rowKey ? " row-dragging" : ""}${rowClassName?.(row) ? ` ${rowClassName(row)}` : ""}`}
                                                onClick={onRowClick ? (e) => {
                                                    if (e.target.closest('.td-checkbox, .col-actions, a, button')) return;
                                                    onRowClick(row);
                                                } : undefined}
                                                onDoubleClick={onRowDoubleClick ? (e) => {
                                                    if (e.target.closest('.td-checkbox, .col-actions')) return;
                                                    onRowDoubleClick(row);
                                                } : undefined}
                                                draggable={!!dragData}
                                                onDragStart={dragData ? (e) => {
                                                    if (e.target.closest('.td-checkbox, .col-actions, a, button')) { e.preventDefault(); return; }
                                                    e.dataTransfer.effectAllowed = "copy";
                                                    e.dataTransfer.setData(dragMimeType, JSON.stringify(dragData));
                                                    setDraggingRowId(rowKey);
                                                } : undefined}
                                                onDragEnd={dragData ? () => setDraggingRowId(null) : undefined}
                                            >
                                                {multiSelect && (
                                                    <td className="td-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input form-check-input-checkbox"
                                                            checked={selectAllPages || selectedIds.includes(resolveId(row, rowId))}
                                                            onChange={() => toggleSelectOne(resolveId(row, rowId))}
                                                        />
                                                    </td>
                                                )}

                                                {displayedColumns.map((col) => (
                                                    <td
                                                        key={col.__key}
                                                        className={col.className || ''}
                                                        style={{ maxWidth: col.maxWidth || 'auto', minWidth: col.minWidth, width: col.width }}
                                                    >
                                                        {col.Cell ? col.Cell(row) : row[col.accessor]}
                                                    </td>
                                                ))}
                                            </tr>
                                            );
                                        })}
                                        <tr>
                                            <td colSpan={displayedColumns.length + (multiSelect ? 1 : 0)} className="table-sentinel">
                                                {loading && rows.length > 0 && <InlineLoader small />}
                                            </td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {enableCardView && effectiveViewMode === "card" && (
                    <div className="table-responsive" ref={scrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
                        {loading && rows.length === 0 ? (
                            <InlineLoader />
                        ) : !loading && rows.length === 0 ? (
                            <div className="table-empty">{emptyMessage}</div>
                        ) : (
                            <>
                                <div className="table-card-grid row g-3 p-3">
                                    {rows.map((row, i) => (
                                        <div className="col-12 col-sm-6 col-md-3" key={resolveId(row, rowId) || i}>
                                            {renderCard ? renderCard(row) : (
                                                <div className="card h-100 shadow-sm">
                                                    <div className="card-body">
                                                        {displayedColumns.map((col) => (
                                                            <p key={col.__key}>
                                                                <strong>{col.Header}:</strong>{" "}
                                                                {col.Cell ? col.Cell(row) : row[col.accessor]}
                                                            </p>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {loading && <InlineLoader small />}
                            </>
                        )}
                    </div>
                )}

            </div>

            {paged && totalPages > 1 && (
                <div className="table-pager">
                    <button
                        type="button"
                        className="table-pager__btn"
                        onClick={() => setCurrentPage(Math.max(0, safePage - 1))}
                        disabled={safePage === 0}
                    >
                        Précédent
                    </button>
                    <span className="table-pager__info">Page {safePage + 1} / {totalPages}</span>
                    <button
                        type="button"
                        className="table-pager__btn"
                        onClick={() => setCurrentPage(Math.min(totalPages - 1, safePage + 1))}
                        disabled={safePage >= totalPages - 1}
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
});

export { ViewModeToggle };
export default Table;